import React, { useState } from 'react';
import { useProducerWithThunks } from 'common/hooks/useProducer';
import {
  CardContents,
  CardTitle,
  TitleContainer,
  CancelButton,
  ButtonContainer,
  PrimaryButton,
  FormError,
  InputContainer,
  AmountInput,
  InputBox,
  Padding,
} from 'common/components/cardStyles';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import {
  doCurrencyConvert,
  doReconcileCustomer,
  doSettleCustomer,
} from 'common/state/customers/thunkActions';
import {
  currencyConvertRecipe,
  reconcileCustomerRecipe,
  settleCustomerRecipe,
} from 'common/state/customers/recipes';
import { defaultAPIRequestState } from 'api';
import { CustomerOutstanding } from 'common/state/customers/actions';
import { Select } from '@mollybet/ui';
import { CurrencyCodes } from 'common/configs/currency';
import { Currency } from 'common/classes/currency';

export interface SettleRow {
  username: string;
  currency: string;
  amount: string;
}

interface SettleModal {
  selectedRow: SettleRow;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

const DEFAULT_DESCRIPTION = 'Manually settled amount';

export function CustomerSettleModal({
  selectedRow,
  setShouldRefresh,
  closeModal,
}: SettleModal) {
  const { username, currency, amount: _amount } = selectedRow;

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(_amount);
  const [description, setDescription] = useState(DEFAULT_DESCRIPTION);

  // The state of the clear request
  const [state, dispatch] = useProducerWithThunks(
    settleCustomerRecipe,
    defaultAPIRequestState
  );

  // Deposit request callback for form
  const submitReconcileClear: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        doSettleCustomer(username, {
          amount,
          currency,
          date,
          description,
          post_date: date,
        })
      );
    },
    [dispatch, amount, username, date, description, currency]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitReconcileClear}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Settle Reconciled Amounts: {username}</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>

        <InputContainer>
          <AmountInput
            currency={currency}
            shrinked
            label="Amount *"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <InputBox
            label="Description *"
            value={description}
            shrinked
            onChange={(e) => setDescription(e.target.value)}
          />
        </InputContainer>
        <InputContainer>
          <Padding />
          <InputBox
            type="date"
            label="Date *"
            value={date}
            shrinked
            onChange={(e) => setDate(e.target.value)}
          />
        </InputContainer>
        <ButtonContainer>
          <PrimaryButton type="submit">Confirm</PrimaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}

export interface ReconcileRow {
  username: string;
  currency: string;
  system?: string;
  account?: string;
  stream?: string;
  threshold?: string;
  amount: string;
}

interface ReconcileModal {
  selectedRow: ReconcileRow;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

export function CustomerReconcileModal({
  selectedRow,
  setShouldRefresh,
  closeModal,
}: ReconcileModal) {
  const {
    username,
    currency,
    amount: _amount,
    threshold,
    system,
    account,
    stream,
  } = selectedRow;

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState(_amount);

  // The state of the clear request
  const [state, dispatch] = useProducerWithThunks(
    reconcileCustomerRecipe,
    defaultAPIRequestState
  );

  // Deposit request callback for form
  const submitReconcileClear: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        doReconcileCustomer(username, {
          amount,
          currency,
          date,
          account,
          system,
          stream,
          post_date: date,
        })
      );
    },
    [dispatch, amount, username, date, account, system, stream, currency]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitReconcileClear}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Reconcile {username}</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>

        <InputContainer>
          <div>Account: {account || 'All Accounts'}</div>
          <div>System: {system || 'All Systems'}</div>
          <div>Stream: {stream || 'All Streams'}</div>
        </InputContainer>
        <InputContainer>
          <AmountInput
            currency={currency}
            shrinked
            label="Amount *"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            help={
              threshold && parseFloat(amount) > parseFloat(threshold)
                ? `Amount is greater than settlement threshold (${new Currency(
                    threshold,
                    currency
                  ).toString()}).`
                : null
            }
          />
          <InputBox
            type="date"
            label="Date *"
            value={date}
            shrinked
            onChange={(e) => setDate(e.target.value)}
          />
        </InputContainer>
        <ButtonContainer>
          <PrimaryButton type="submit">Confirm</PrimaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}

interface CurrencyConvertModalProps {
  selectedRow: CustomerOutstanding;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

const CURRENCY_OPTIONS = Object.values(CurrencyCodes).map((currencyCode) => ({
  value: currencyCode,
  label: currencyCode,
}));

export function CurrencyConvertModal({
  selectedRow,
  setShouldRefresh,
  closeModal,
}: CurrencyConvertModalProps) {
  const {
    account_username,
    customer_username,
    stream,
    system,
    currency: _currency,
    amount: _amount,
  } = selectedRow;

  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [currency, setCurrency] = useState(_currency);
  const [amount, setAmount] = useState(_amount);
  const [toCurrency, setToCurrency] = useState(_currency);
  const [toAmount, setToAmount] = useState(_amount);

  // The state of the clear request
  const [state, dispatch] = useProducerWithThunks(
    currencyConvertRecipe,
    defaultAPIRequestState
  );

  // Deposit request callback for form
  const submitReconcileClear: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        doCurrencyConvert(customer_username, {
          from_amount: amount,
          from_currency: currency,
          to_amount: toAmount,
          to_currency: toCurrency,
          stream,
          system,
          account: account_username,
          post_date: date,
        })
      );
    },
    [
      dispatch,
      customer_username,
      amount,
      currency,
      toAmount,
      toCurrency,
      date,
      stream,
      system,
      account_username,
    ]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submitReconcileClear}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Currency Convert</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>

        <InputContainer>
          <AmountInput
            currency={currency}
            shrinked
            label="From Amount *"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <Select
            label="From Currency *"
            options={CURRENCY_OPTIONS}
            value={{ value: currency, label: currency }}
            shrinked
            onChange={(option) => setCurrency(option.value)}
          />
        </InputContainer>
        <InputContainer>
          <AmountInput
            currency={currency}
            shrinked
            label="To Amount *"
            value={toAmount}
            onChange={(e) => setToAmount(e.target.value)}
          />
          <Select
            label="To Currency *"
            options={CURRENCY_OPTIONS}
            value={{ value: currency, label: currency }}
            shrinked
            onChange={(option) => setToCurrency(option.value)}
          />
        </InputContainer>
        <InputContainer>
          <Padding />
          <InputBox
            type="date"
            label="Date *"
            value={date}
            shrinked
            onChange={(e) => setDate(e.target.value)}
          />
        </InputContainer>

        <ButtonContainer>
          <PrimaryButton type="submit">Confirm</PrimaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}
